import type { ColumnDef } from "@tanstack/react-table";

import type { BaseRowItem } from "../../components/data-table";
import type {
  AdminBusinessTransaction,
  BusinessTransactionType,
} from "../../lib/api/adminBusinessTransactions";
import { formatAmount } from "../../utils/FormatPerson";

export const MERCHANT_TABLE_LIMIT = 25;

export type MerchantTransactionRow = BaseRowItem & {
  raw: AdminBusinessTransaction;
  displayDate: string;
  statusLabel: string;
  businessName: string;
  businessTitle: string;
  ownerName: string;
  ownerTitle: string;
  /** Deposits: asset + native amount. Payouts: asset + native amount. */
  assetLabel: string;
  /** Deposits: USD value. Payouts: NGN payout amount. */
  amountLabel: string;
  /** Deposits: payment page name. Payouts: bank + masked account. */
  detailLine: string;
  detailTitle: string;
  reference: string;
};

function str(v: unknown): string {
  return typeof v === "string" ? v : "";
}

function num(v: unknown): number | undefined {
  if (typeof v === "number" && !Number.isNaN(v)) return v;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    return Number.isNaN(n) ? undefined : n;
  }
  return undefined;
}

function obj(v: unknown): Record<string, unknown> | null {
  return v && typeof v === "object" ? (v as Record<string, unknown>) : null;
}

export function getMerchantDisplayDate(tx: AdminBusinessTransaction): string {
  const iso = str(tx.processedAt) || str(tx.createdAt) || str(tx.updatedAt);
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

export function merchantStatusBadgeClass(statusRaw: unknown): string {
  const u = str(statusRaw).toUpperCase();
  if (u === "COMPLETED" || u === "SUCCESSFUL") {
    return "bg-emerald-500/15 text-emerald-400 border-emerald-500/35";
  }
  if (u === "FAILED") {
    return "bg-red-500/15 text-red-300 border-red-500/35";
  }
  if (u === "PENDING" || u === "PROCESSING") {
    return "bg-amber-500/15 text-amber-300 border-amber-500/35";
  }
  return "bg-[#2C363E] text-[#AEBCC6] border-[#3A4852]";
}

export function businessStatusBadgeClass(statusRaw: unknown): string {
  const u = str(statusRaw).toUpperCase();
  if (u === "APPROVED") {
    return "bg-emerald-500/15 text-emerald-400 border-emerald-500/35";
  }
  if (u === "REJECTED") {
    return "bg-red-500/15 text-red-300 border-red-500/35";
  }
  if (u === "PENDING") {
    return "bg-amber-500/15 text-amber-300 border-amber-500/35";
  }
  return "bg-[#2C363E] text-[#AEBCC6] border-[#3A4852]";
}

function businessFromTx(tx: AdminBusinessTransaction): {
  name: string;
  title: string;
} {
  const b = obj(tx.business);
  if (!b) return { name: "—", title: "—" };
  const name = str(b.name).trim();
  const email = str(b.email).trim();
  const type = str(b.type).trim();
  return {
    name: name || email || "—",
    title: [name, email, type].filter(Boolean).join(" · ") || "—",
  };
}

export function ownerFromTx(tx: AdminBusinessTransaction): {
  name: string;
  title: string;
} {
  const o = obj(tx.owner);
  if (!o) return { name: "—", title: "—" };
  const profile = obj(o.profile);
  const first = profile ? str(profile.firstName) : "";
  const last = profile ? str(profile.lastName) : "";
  const username = profile ? str(profile.username) : "";
  const email = str(obj(o.email)?.address);
  const name = [first, last].filter(Boolean).join(" ").trim();
  const display = name || username || email || "—";
  return {
    name: display,
    title: [name, username, email].filter(Boolean).join(" · ") || "—",
  };
}

/** Payout rows nest the asset; deposit rows carry currency/amount flat. */
export function assetLabelFor(tx: AdminBusinessTransaction): string {
  const asset = obj(tx.asset);
  if (asset) {
    const name = str(asset.name).toUpperCase();
    const amount = num(asset.amount);
    if (name && amount != null) return `${formatAmount(amount)} ${name}`;
    return name || "—";
  }
  const currency = str(tx.currency).toUpperCase();
  const amount = num(tx.amount);
  if (currency && amount != null) return `${formatAmount(amount)} ${currency}`;
  return currency || "—";
}

function depositAmountLabel(tx: AdminBusinessTransaction): string {
  const usd = num(tx.usdtAmount) ?? num(tx.amountInUSD);
  return usd != null ? `$${formatAmount(usd)}` : "—";
}

function payoutAmountLabel(tx: AdminBusinessTransaction): string {
  const ngn = num(tx.payoutAmount);
  return ngn != null ? `₦${formatAmount(ngn)}` : "—";
}

function depositDetail(tx: AdminBusinessTransaction): {
  line: string;
  title: string;
} {
  const page = obj(tx.paymentPage);
  const pageName = page ? str(page.name).trim() : "";
  const network = str(tx.network).toUpperCase();
  const pageAmount = page ? num(page.amount) : undefined;
  const line = [pageName || "Direct deposit", network].filter(Boolean).join(" · ");
  const title = [
    pageName ? `Page: ${pageName}` : "No payment page",
    network ? `Network: ${network}` : "",
    pageAmount != null ? `Page amount: ₦${formatAmount(pageAmount)}` : "",
    page && str(page.reference) ? `Ref: ${str(page.reference)}` : "",
  ]
    .filter(Boolean)
    .join(" · ");
  return { line: line || "—", title: title || "—" };
}

function payoutDetail(tx: AdminBusinessTransaction): {
  line: string;
  title: string;
} {
  const r = obj(tx.recipient);
  if (!r) return { line: "—", title: "—" };
  const bank = str(r.bankName).trim();
  const account = str(r.accountNumber).trim();
  const accountName = str(r.accountName).trim();
  const masked = account ? `••••${account.slice(-4)}` : "";
  const line = [bank, masked].filter(Boolean).join(" · ");
  const title = [accountName, bank, account].filter(Boolean).join(" · ");
  return { line: line || "—", title: title || "—" };
}

export function buildMerchantTransactionRows(
  transactions: AdminBusinessTransaction[],
  type: BusinessTransactionType
): MerchantTransactionRow[] {
  const isDeposit = type === "deposits";
  return transactions.map((tx) => {
    const business = businessFromTx(tx);
    const owner = ownerFromTx(tx);
    const detail = isDeposit ? depositDetail(tx) : payoutDetail(tx);
    return {
      id: tx._id,
      raw: tx,
      displayDate: getMerchantDisplayDate(tx),
      statusLabel: str(tx.status) || "—",
      businessName: business.name,
      businessTitle: business.title,
      ownerName: owner.name,
      ownerTitle: owner.title,
      assetLabel: assetLabelFor(tx),
      amountLabel: isDeposit ? depositAmountLabel(tx) : payoutAmountLabel(tx),
      detailLine: detail.line,
      detailTitle: detail.title,
      reference: str(tx.reference) || str(tx.txid) || "—",
    };
  });
}

function truncatedCell(value: string, title: string, maxWidth: string) {
  return (
    <span
      className={`text-adminTable-muted truncate ${maxWidth} inline-block align-bottom`}
      title={title}
    >
      {value}
    </span>
  );
}

export function createMerchantTransactionColumns(
  type: BusinessTransactionType,
  onView: (row: MerchantTransactionRow) => void
): ColumnDef<MerchantTransactionRow, unknown>[] {
  const isDeposit = type === "deposits";

  return [
    {
      accessorKey: "displayDate",
      header: "Date",
      meta: { hideBelow: "md" as const },
      cell: ({ getValue }) => (
        <span className="text-adminTable-muted">{String(getValue())}</span>
      ),
    },
    {
      accessorKey: "businessName",
      header: "Business",
      meta: { wrap: true, maxWidth: "220px" },
      cell: ({ row }) => (
        <span className="text-[13px] text-white" title={row.original.businessTitle}>
          {row.original.businessName}
        </span>
      ),
    },
    {
      accessorKey: "assetLabel",
      header: "Asset",
      cell: ({ getValue }) => (
        <span className="text-[13px] text-white tabular-nums">
          {String(getValue())}
        </span>
      ),
    },
    {
      accessorKey: "amountLabel",
      header: isDeposit ? "USD value" : "Payout (NGN)",
      meta: { align: "right" as const },
      cell: ({ getValue }) => (
        <span className="text-[13px] font-semibold text-[#BEE1FB] tabular-nums">
          {String(getValue())}
        </span>
      ),
    },
    {
      accessorKey: "detailLine",
      header: isDeposit ? "Payment page" : "Recipient",
      meta: { hideBelow: "lg" as const, wrap: true, maxWidth: "240px" },
      cell: ({ row }) => (
        <span className="text-[13px] text-adminTable-muted" title={row.original.detailTitle}>
          {row.original.detailLine}
        </span>
      ),
    },
    {
      accessorKey: "ownerName",
      header: "Owner",
      meta: { hideBelow: "lg" as const },
      cell: ({ row }) =>
        truncatedCell(row.original.ownerName, row.original.ownerTitle, "max-w-[180px]"),
    },
    {
      accessorKey: "statusLabel",
      header: "Status",
      cell: ({ row }) => (
        <span
          className={`inline-flex rounded-full border px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${merchantStatusBadgeClass(
            row.original.raw.status
          )}`}
        >
          {row.original.statusLabel}
        </span>
      ),
    },
    {
      id: "actions",
      header: "",
      meta: { align: "right" as const },
      cell: ({ row }) => (
        <button
          type="button"
          className="rounded-lg border border-[#3A4852] bg-[#1C2227] px-3 py-1.5 text-[12px] font-semibold text-secondary_button hover:border-secondary_button hover:bg-[#273037]"
          onClick={() => onView(row.original)}
        >
          View
        </button>
      ),
    },
  ];
}
